/**
 * Notification Store
 * 统一管理通知状态
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { notificationApi, type NotificationItem, type NotificationQuery } from '@/api/notification'
import { NotificationPriorityLabel } from '@/api/notification'
import { useWebSocket } from '@/composables/useWebSocket'
import logger from '@/utils/logger'

interface WebSocketNotificationData {
  id: number
  title: string
  content: string
  notificationType: string
  priority: number
  relatedType?: string
  relatedId?: number
  actionType?: string
  actionUrl?: string
  createTime: string
}

export const useNotificationStore = defineStore(
  'notification',
  () => {
    // 一次性清理历史遗留的持久化脏数据（旧版 persist 曾缓存未读数到 localStorage，
    // 导致死循环期间累加的巨量脏值无法随刷新归零）。persist 已移除，此 key 不再写入，
    // 此处清除仅作用于升级过渡期，确保旧账号脏数据不再展示。
    try {
      localStorage.removeItem('notification-store')
    } catch {
      /* 忽略：某些隐私模式下 localStorage 不可用 */
    }

    // ==================== State ====================

    /**
     * 通知列表
     */
    const notifications = ref<NotificationItem[]>([])

    /**
     * 未读数量
     */
    const unreadCount = ref(0)

    /**
     * 分页总数量 / 全部消息数量
     * 修复: P0 - 合并 totalCount 和 total 为单一状态变量
     * 之前使用 totalCount 和 total 两个变量存储同一个值，造成冗余
     * 现在统一使用 total 表示分页总数，也作为全部消息数量
     */
    const total = ref(0)

    /**
     * 当前页码
     */
    const currentPage = ref(1)

    /**
     * 每页数量
     */
    const pageSize = ref(20)

    /**
     * 加载状态
     */
    const loading = ref(false)

    /**
     * 错误信息
     */
    const error = ref<string | null>(null)

    /**
     * 当前激活的标签页
     */
    const activeTab = ref<'all' | 'unread' | 'system' | 'task'>('all')

    /**
     * 优先级筛选
     */
    const priorityFilter = ref<number | undefined>()

    /**
     * WebSocket 实例
     */
    let wsInstance: ReturnType<typeof useWebSocket> | null = null

    // ==================== Getters ====================

    /**
     * 未读通知列表
     */
    const unreadNotifications = computed(() =>
      notifications.value.filter(n => n.unread)
    )

    /**
     * 按类型筛选的通知
     */
    const notificationsByType = computed(() => {
      return (type: string) =>
        notifications.value.filter(n => n.notificationType === type)
    })

    /**
     * 是否有未读通知
     */
    const hasUnread = computed(() => unreadCount.value > 0)

    // ==================== Actions ====================

    /**
     * 获取通知列表
     * 修复: P0 - 返回通知列表数据，解决MainLayout通知面板无法显示的问题
     */
    async function fetchNotifications(params?: NotificationQuery) {
      loading.value = true
      error.value = null
      try {
        const queryParams: NotificationQuery = {
          unreadOnly: activeTab.value === 'unread',
          type: getTypeParam(activeTab.value),
          priority: priorityFilter.value,
          page: currentPage.value,
          pageSize: pageSize.value,
          ...params
        }

        const response = await notificationApi.list(queryParams)
        notifications.value = response?.list || []
        total.value = response?.total || 0

        // 返回通知列表供调用方使用
        return response?.list || []
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any，通过类型守卫获取错误信息
        const message = err instanceof Error ? err.message : '加载通知失败'
        error.value = message
        logger.error('加载通知失败:', err)
        throw err
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取未读数量
     */
    async function fetchUnreadCount() {
      try {
        const count = await notificationApi.getUnreadCount()
        unreadCount.value = count || 0
        return count
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any
        logger.error('加载未读数量失败:', err)
        // 接口失败时显式归零：未读数以服务端为准，宁可显示 0 也不展示历史脏值
        unreadCount.value = 0
        return 0
      }
    }

    /**
     * 标记为已读
     */
    async function markAsRead(id: number) {
      try {
        await notificationApi.markAsRead(id)
        // 更新本地状态
        const notification = notifications.value.find(n => n.id === id)
        if (notification && notification.unread) {
          notification.unread = false
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        }
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any
        logger.error('标记已读失败:', err)
        throw err
      }
    }

    /**
     * 批量标记为已读
     */
    async function batchMarkAsRead(ids: number[]) {
      try {
        await notificationApi.batchMarkAsRead(ids)
        // 更新本地状态
        ids.forEach(id => {
          const notification = notifications.value.find(n => n.id === id)
          if (notification && notification.unread) {
            notification.unread = false
          }
        })
        // 重新获取未读数量
        await fetchUnreadCount()
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any
        logger.error('批量标记已读失败:', err)
        throw err
      }
    }

    /**
     * 全部标记为已读
     */
    async function markAllAsRead() {
      try {
        await notificationApi.markAllAsRead()
        // 更新本地状态
        notifications.value.forEach(n => {
          n.unread = false
        })
        unreadCount.value = 0
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any
        logger.error('全部标记已读失败:', err)
        throw err
      }
    }

    /**
     * 删除通知
     */
    async function deleteNotification(id: number) {
      try {
        await notificationApi.delete(id)
        // 从列表中移除
        const index = notifications.value.findIndex(n => n.id === id)
        if (index > -1) {
          const notification = notifications.value[index]
          notifications.value.splice(index, 1)
          total.value = Math.max(0, total.value - 1)
          // 如果是未读通知，更新未读数量
          if (notification?.unread) {
            unreadCount.value = Math.max(0, unreadCount.value - 1)
          }
        }
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any
        logger.error('删除通知失败:', err)
        throw err
      }
    }

    /**
     * 删除所有通知
     */
    async function deleteAll() {
      try {
        const deletedCount = await notificationApi.deleteAll()
        notifications.value = []
        unreadCount.value = 0
        total.value = 0
        return deletedCount
      } catch (err: unknown) {
        // 修复: P0 - 使用 unknown 替代 any
        logger.error('清空通知失败:', err)
        throw err
      }
    }

    /**
     * 刷新通知数据
     */
    async function refresh() {
      await fetchUnreadCount()
      await fetchNotifications()
    }

    /**
     * 获取类型参数
     */
    function getTypeParam(tab: string): string | undefined {
      const typeMap: Record<string, string | undefined> = {
        'system': 'SYSTEM',
        'task': 'TASK_REMINDER',
        'all': undefined,
        'unread': undefined
      }
      return typeMap[tab]
    }

    /**
     * 设置当前页码
     */
    function setPage(page: number) {
      currentPage.value = page
    }

    /**
     * 设置每页数量
     */
    function setPageSize(size: number) {
      pageSize.value = size
    }

    /**
     * 设置激活标签
     */
    function setActiveTab(tab: 'all' | 'unread' | 'system' | 'task') {
      activeTab.value = tab
    }

    /**
     * 设置优先级筛选
     */
    function setPriorityFilter(priority?: number) {
      priorityFilter.value = priority
    }

    /**
     * 重置分页
     */
    function resetPagination() {
      currentPage.value = 1
      pageSize.value = 20
    }

    /**
     * 重置所有状态
     */
    function reset() {
      notifications.value = []
      unreadCount.value = 0
      total.value = 0
      currentPage.value = 1
      pageSize.value = 20
      loading.value = false
      error.value = null
      activeTab.value = 'all'
      priorityFilter.value = undefined
    }

    /**
     * 初始化 WebSocket 连接
     */
    function initWebSocket() {
      if (wsInstance) {
        return // 避免重复初始化
      }

      wsInstance = useWebSocket({
        onNotification: handleWebSocketNotification,
        onConnected: () => {
          logger.debug('WebSocket: 连接成功，刷新未读数量')
          fetchUnreadCount()
        },
        onDisconnected: () => {
          logger.debug('WebSocket: 连接断开')
        }
      })

      // 连接 WebSocket
      wsInstance.connect()
    }

    /**
     * 断开 WebSocket 连接
     */
    function disconnectWebSocket() {
      if (wsInstance) {
        wsInstance.disconnect()
        wsInstance = null
      }
    }

    /**
     * 处理 WebSocket 收到的通知
     * 修复: P0 - WebSocket 消息不包含所有字段，使用合理的默认值
     * 修复: 按 id 去重，避免重复计数（修复通知推送死循环导致的前端放大）
     */
    function handleWebSocketNotification(data: WebSocketNotificationData) {
      // 去重：相同 id 不重复计入/入列
      const exists = notifications.value.some(n => n.id === data.id)
      if (!exists) {
        unreadCount.value++
        total.value++

        /**
         * WebSocket 推送的通知与 REST API 返回的字段差异说明：
         * - senderName: WebSocket 不提供，使用空字符串（前端显示时可以隐藏或显示"系统"）
         * - receiverId: WebSocket 不需要（通知是推送给当前用户的），使用 0 作为占位值
         * - isRead: WebSocket 推送的新通知默认未读，使用 0
         * - readTime: 未读通知无阅读时间，使用 null
         * - expireTime: WebSocket 消息不包含过期时间，使用 null
         * - expired: WebSocket 消息不包含过期状态，默认 false
         * - displayTime: 前端根据 createTime 计算显示时间
         * - notificationTypeName: 前端根据 notificationType 映射
         * - unread: WebSocket 推送的都是未读通知，设为 true
         * - important: 根据 priority > 0 判断是否重要
         */
        const newNotification: NotificationItem = {
          id: data.id,
          notificationType: data.notificationType,
          notificationTypeName: getNotificationTypeName(data.notificationType),
          title: data.title,
          content: data.content,
          senderName: '', // WebSocket 不提供发送人信息，默认为空
          receiverId: 0, // WebSocket 不需要接收人ID，使用 0 占位
          relatedType: data.relatedType,
          relatedId: data.relatedId,
          priority: data.priority,
          isRead: 0, // 新通知默认未读
          readTime: undefined, // 未读通知无阅读时间
          actionType: data.actionType,
          actionUrl: data.actionUrl,
          expireTime: undefined, // WebSocket 消息不包含过期时间
          createTime: data.createTime,
          unread: true, // WebSocket 推送的都是未读通知
          expired: false, // 默认未过期
          important: data.priority > 0, // 根据优先级判断是否重要
          displayTime: formatDisplayTime(new Date(data.createTime)) // 前端计算显示时间
        }

        notifications.value.unshift(newNotification)

        // 限制列表长度
        if (notifications.value.length > 50) {
          notifications.value = notifications.value.slice(0, 50)
        }
      }
    }

    /**
     * 获取通知类型名称
     */
    function getNotificationTypeName(typeCode: string): string {
      const typeMap: Record<string, string> = {
        'SYSTEM': '系统通知',
        'APPROVAL': '审批通知',
        'TASK_REMINDER': '任务提醒'
      }
      return typeMap[typeCode] || typeCode
    }

    /**
     * 格式化显示时间
     */
    function formatDisplayTime(date: Date | string): string {
      const now = new Date()
      const time = typeof date === 'string' ? new Date(date) : date
      const diffMs = now.getTime() - time.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return '刚刚'
      if (diffMins < 60) return `${diffMins}分钟前`
      if (diffHours < 24) return `${diffHours}小时前`
      if (diffDays < 2) return '昨天'
      if (diffDays < 7) return `${diffDays}天前`

      return time.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    return {
      // State
      notifications,
      unreadCount,
      total,
      currentPage,
      pageSize,
      loading,
      error,
      activeTab,
      priorityFilter,

      // Getters
      unreadNotifications,
      notificationsByType,
      hasUnread,

      // Actions
      fetchNotifications,
      fetchUnreadCount,
      markAsRead,
      batchMarkAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAll,
      refresh,
      getTypeParam,
      setPage,
      setPageSize,
      setActiveTab,
      setPriorityFilter,
      resetPagination,
      reset,
      initWebSocket,
      disconnectWebSocket
    }
  }
  /**
   * 修复: 移除 persist 持久化配置（原本将 unreadCount 缓存到 localStorage）
   *
   * 根因：unreadCount 属于服务端权威数据，一旦被持久化到本地：
   *  1. 历史通知推送死循环期间累加出的巨大脏值（曾出现 95万+）会永久残留在浏览器中；
   *  2. fetchUnreadCount() 接口失败时仅 return 0 而不重置 state，脏值继续展示；
   *  3. 换账号/退出登录后旧账号的未读数仍会显示给新账号。
   *
   * 修复策略：不做本地持久化，每次页面加载由 MainLayout onMounted -> refreshNotifications()
   * 从后端拉取真实未读数（fetchUnreadCount），失败时显式归零，保证永远与服务端一致。
   */
)

// 导出类型和常量供组件使用
export { NotificationPriorityLabel }
export type { NotificationItem, NotificationQuery }
